import type { ReactNode } from 'react';
import type { OperatorGeneralConfigResponseData } from '@alien-platform/bo-core';
import { Media } from '@alien-platform/cms-bo';
import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { type AlertColor } from '@mui/material';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type OptionsObject, type SnackbarMessage } from 'notistack';

import { ModalDrawer, PlayerSubInfo, type GeneralDrawer, type GeneralModal } from '@/types/general';

import { type RootState } from '../index';

export interface ModalDrawerProps {
  open: ModalDrawer;
  content?: unknown;
  extraData?: unknown;
  header?: { title?: string; icon?: IconDefinition };
  previousModalDrawer?: ModalDrawerProps;
}

interface ToastProps {
  message: SnackbarMessage;
  options?: OptionsObject;
  persist?: boolean;
}

interface AlertProps {
  message: string;
  severity: AlertColor;
}

interface SelectedMediaProps {
  media: Media | undefined;
  target: Record<string, unknown>;
}
interface SelectedPageProps {
  slug: string | null;
  target: string;
  index?: number;
}

interface UISliceProps {
  lng: string;
  modal: ModalDrawerProps | null;
  alert: AlertProps | null;
  drawerState: Record<GeneralDrawer, boolean>;
  modalState: Record<GeneralModal, boolean>;
  toast: ToastProps | null;
  generalConfig: OperatorGeneralConfigResponseData | null;
  content?: ReactNode;
  // navBarTitle: string | null
  selectedMediaItems: SelectedMediaProps | null;
  selectedPageItems: SelectedPageProps | null;
  selectedLanguage: string;
  selectedCmsItem: unknown;
  enableAffiliateClaimProfit: boolean;
  // Deprecated, storing this value in auth0 user
  activePlayerSubInfo: PlayerSubInfo;
  toggleRefresh: boolean;
  messageBoxStatus: { isDisabled: boolean; isNew: boolean };
  ticketDetailsLoader: boolean;
}

const initialState: UISliceProps = {
  lng: 'en',
  modal: null,
  alert: null,
  drawerState: {
    PLAYER_CASHBACK_TRANSACTIONS_LIST: false,
    BET_DETAILS: false,
    CHANNEL_DETAILS: false,
    GENERAL_GAME_DETAILS: false,
    OPERATOR_GAME_DETAILS: false,
    PLAYER_DETAILS: false,
    ADMIN_DETAILS: false,
    TABLE_FILTERS: false,
    TRANSACTION_DETAILS: false,
    SUPPORT_TICKET_DETAILS: false,
    SUPPORT_TICKET_TEMPLATES: false,
    PLAYER_SUPPORT_TICKET_DETAILS: false,
    NAV_DRAWER: false,
    OPERATOR_DETAILS: false,
    AFFILIATE_DETAILS: false,
    NEW_AFFILIATE: false,
    NEW_ADMIN: false,
    NEW_DEPOSIT_GATEWAY: false,
    NEW_DEPOSIT_GATEWAY_V2: false,
    NEW_WITHDRAW_GATEWAY_V2: false,
    NEW_TAG: false,
    FEATURED_CARD_DETAILS: false,
    SELECTED_BANNER_DETAILS: false,
    SELECTED_CMS_MODAL_DETAILS: false,
    REFERRAL_CLAIM_HISTORY: false,
    REFERRED_PLAYERS_LIST: false,
    OPERATOR_BILLING_RECORD_DETAILS: false,
    BRAND_BILLING_RECORD_DETAILS: false,
    AFFILIATE_BILLING_RECORD_DETAILS: false,
    OPERATOR_PAYMENT_FORM: false,
    OPERATOR_GENERATE_INVOICE_FORM: false,
    BRAND_GENERATE_INVOICE_FORM: false,
    BRAND_PAYMENT_FORM: false,
    AFD_DETAILS: false,
    DNS_RECORD: false,
    AFFILIATE_CLAIM_HISTORY: false,
    AFFILIATE_WITHDRAWAL_HISTORY: false,
    OPERATOR_AFFILIATE_PAYMENT_FORM: false,
    PLAYER_BONUS_DETAILS: false,
    PLAYER_BONUS_RELEASE_DETAILS: false,
    BANK_ACCOUNT: false,
    AFFILATE_MONTHLY_STATS_DETAILS: false,
    CF_TOKEN_DETAILS: false,
    SPORT_FREEBET_DETAILS: false,
    SPORT_FREEBET_TEMPLATE_LIST: false,
    CF_ZONE_DETAILS: false,
    PLAYER_APPLIED_FREEBET_DETAILS: false,
    ROLE_DETAILS: false,
    PLAY_GAME: false,
    BONUS_RELEASE_REPORT_DETAILS: false,
    ACTIVITIES_TRANSACTION_DETAILS: false,
    AFFILIATE_ACTIVE_PLAYERS: false,
    OPERATOR_BILLING_INVOICE_DETAILS: false,
    PLAYER_PROFIT_TRANSACTIONS_LIST: false,
    DOMAIN_DETAILS: false,
    CF_ACCOUNT_DETAILS: false,
    CF_ZONE_DETAILS_V2: false,
    STAFF_DETAILS: false,
    KYC_DETAILS: false,
    NEW_TOURNAMENT: false,
    NEW_BONUS_DEFINITION: false,
    APPEARANCE: false,
  },
  modalState: {
    PLAYER_DELETE_CONFIRMATION: false,
    VERIFY_DEPOSIT_CONFIRMATION: false,
    APPROVE_REJECT_WITHDRAW_CONFIRMATION: false,
    APPROVE_REFERRAL_CLAIM_CONFIRMATION: false,
    EDIT_OPERATOR_BILLING_RECORD_PAYMENT: false,
    EDIT_OPERATOR_BILLING_RECORD_INVOICE: false,
    DELETE_OPERATOR_BILLING_RECORD_INVOICE: false,
    DELETE_OPERATOR_BILLING_RECORD_PAYMENT: false,
    DELETE_BRAND_BILLING_RECORD_INVOICE: false,
    DELETE_BRAND_BILLING_RECORD_PAYMENT: false,
    EDIT_BRAND_BILLING_RECORD_INVOICE: false,
    EDIT_BRAND_BILLING_RECORD_PAYMENT: false,
    DELETE_TRANSACTION: false,
    DELETE_AFFILIATE_BILLING_RECORD_PAYMENT: false,
    EDIT_AFFILIATE_BILLING_RECORD_PAYMENT: false,
    APPROVE_REJECT_BANK_ACCOUNT_CONFIRMATION: false,
    CONVERT_PLAYER_BALANCE: false,
    CHECK_ORDER_STATUS: false,
    EDIT_OPERATOR_BILLING_RECORD_INVOICE_V2: false,
    REJECT_DEPOSIT_ORDER: false,
    ADD_SPORT_FREEBET_CONFIRMATION: false,
    APPROVE_REJECT_BONUS_CONFIRMATION: false,
    OWN_USER_RELOGIN: false,
    STAFF_RELOGIN: false,
    FORCE_APPROVE_JB_WITHDRAW: false,
    HELP_INFO: false,
    INFORMATION_ICON: false,
    LAUNCH_GAME_URL: false,
    ERROR_RESPONSE: false,
    AFFILIATE_WITHDRAW_PROFIT_CONFIRMATION: false,
    PLAYER_SEARCH: false,
    APPROVE_TOMANI_WITHDRAW: false,
    DELETE_DEPOSIT_GATEWAY_CONFIRMATION: false,
    DELETE_WITHDRAW_GATEWAY_CONFIRMATION: false,
    EDIT_OPERATOR_BILLING_INVOICE_SETTING: false,
    EDIT_OPERATOR_BILLING_INVOICE: false,
    OPERATOR_GENERATE_INVOICE_FORM: false,
    OPERATOR_BILLING_PROVIDER_PAYMENT: false,
    PLAYER_CASHBACK_CLAIM_CONFIRMATION: false,
    PLAYER_CASHBACK_OVERRIDE_SETTINGS: false,
    AFFILIATE_PROFIT_CALCULATOR: false,
    PLAYER_TEST_CALCULATE_AFFILIATE_PROFIT: false,
    AFFILIATE_LEVEL_SETTINGS: false,
    PLAYER_CASHBACK_RESTORE_OPERATOR_SETTINGS: false,
    PLAYER_AFFILIATE_OVERRIDE_SETTINGS: false,
    PLAYER_AFFILIATE_RESTORE_OPERATOR_SETTINGS: false,
    APPROVE_REJECT_CASHBACK_CONFIRMATION: false,
    APPROVE_REJECT_AFFILIATE_CONFIRMATION: false,
  },
  toast: null,
  generalConfig: null,
  content: null,
  selectedMediaItems: null,
  selectedPageItems: null,
  selectedLanguage: 'en',
  selectedCmsItem: null,
  toggleRefresh: false,
  enableAffiliateClaimProfit: false,
  activePlayerSubInfo: PlayerSubInfo.USERNAME,
  messageBoxStatus: { isDisabled: false, isNew: false },
  ticketDetailsLoader: false,

  // navBarTitle: null,
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    showModal(state, action: PayloadAction<ModalDrawerProps>) {
      state.modal = action.payload;
    },

    setDrawerState(
      state,
      action: PayloadAction<{
        key: GeneralDrawer;
        value: boolean;
      }>
    ) {
      const { key, value } = action.payload;
      state.drawerState[key] = value;
    },
    showDrawerState(state, action: PayloadAction<GeneralDrawer>) {
      state.drawerState[action.payload] = true;
    },
    setEnableAffiliateClaimProfit(state) {
      state.enableAffiliateClaimProfit = true;
    },
    hideDrawerState(state, action: PayloadAction<GeneralDrawer>) {
      state.drawerState[action.payload] = false;
    },
    showModalState(state, action: PayloadAction<GeneralModal>) {
      state.modalState[action.payload] = true;
    },
    hideModalState(state, action: PayloadAction<GeneralModal>) {
      state.modalState[action.payload] = false;
    },
    hideModal(state) {
      state.modal = null;
    },
    toggleRefresh(state) {
      state.toggleRefresh = !state.toggleRefresh;
    },
    changeSelectedLanguage(state, action: PayloadAction<string>) {
      state.selectedLanguage = action.payload;
    },

    setSelectedCmsItem(state, action: PayloadAction<unknown>) {
      state.selectedCmsItem = action.payload;
    },
    toggleSelectedMediaItems(state, action: PayloadAction<SelectedMediaProps>) {
      // if (state.selectedMediaItems?.media?._id === action.payload.media?._id) {
      //   state.selectedMediaItems = null;
      // } else {
      // }
      state.selectedMediaItems = action.payload;
    },
    toggleSelectedPageItem(state, action: PayloadAction<SelectedPageProps>) {
      if (state.selectedPageItems?.slug === action.payload.slug) {
        state.selectedPageItems = null;
      } else {
        state.selectedPageItems = action.payload;
      }
    },
    resetMediaItems(state) {
      state.selectedMediaItems = null;
    },

    showToast(state, action: PayloadAction<ToastProps>) {
      const typedState = state as UISliceProps;
      typedState.toast = action.payload;
    },
    hideToast(state) {
      state.toast = null;
    },
    showAlert(state, action: PayloadAction<AlertProps>) {
      const typedState = state as UISliceProps;
      typedState.alert = action.payload;
    },
    hideAlert(state) {
      state.alert = null;
    },
    setGeneralConfig(state, action: PayloadAction<OperatorGeneralConfigResponseData>) {
      state.generalConfig = action.payload;
    },
    setActivePlayerSubInfo(state, action: PayloadAction<PlayerSubInfo>) {
      state.activePlayerSubInfo = action.payload;
    },
    setTicketMessageBox: (state, action: PayloadAction<{ isDisabled: boolean; isNew: boolean }>) => {
      state.messageBoxStatus = action.payload;
    },
    setTicketDetailsLoader: (state, action: PayloadAction<boolean>) => {
      state.ticketDetailsLoader = action.payload;
    },
  },
});

export const {
  showModal,
  showModalState,
  hideModalState,
  hideModal,
  showToast,
  hideToast,
  showAlert,
  hideAlert,
  hideDrawerState,
  setDrawerState,
  showDrawerState,
  setGeneralConfig,
  toggleSelectedMediaItems,
  toggleSelectedPageItem,
  resetMediaItems,
  changeSelectedLanguage,
  setSelectedCmsItem,
  setEnableAffiliateClaimProfit,
  setActivePlayerSubInfo,
  toggleRefresh,
  setTicketMessageBox,
  setTicketDetailsLoader,
} = uiSlice.actions;

export const getUi = (state: RootState): UISliceProps => state.ui;

export const getGeneralConfig = (state: RootState): OperatorGeneralConfigResponseData | null => state.ui.generalConfig;
